import { tokenStorage } from './tokenStorage';

/**
 * The single HTTP entry point.
 *
 * Feature modules import {@link apiRequest}; nothing else calls `fetch` directly. That is
 * what keeps token attachment, correlation ids, the refresh dance and the ProblemDetails
 * contract in one place instead of thirty.
 */

const BASE_PATH = '/api/v1';

/** In memory only, never persisted, never logged (`frontend-standards.md` section 5). */
let accessToken: string | null = null;

/** A refresh already in flight, so concurrent 401s produce one refresh rather than five. */
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * A failed request, with the server's structured error attached.
 *
 * The `errorCode` matters more than the message: the simple view renders a localized string
 * chosen from the code and never echoes server prose, because server text is not guaranteed
 * to be free of GitLab vocabulary (`frontend-standards.md` section 6).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly correlationId: string | null;
  readonly fieldErrors: Record<string, string[]>;

  constructor(init: {
    status: number;
    errorCode: string;
    message: string;
    correlationId?: string | null;
    fieldErrors?: Record<string, string[]>;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.errorCode = init.errorCode;
    this.correlationId = init.correlationId ?? null;
    this.fieldErrors = init.fieldErrors ?? {};
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Whether the resource is absent *or* outside the caller's permitted set.
   *
   * The API deliberately does not distinguish those, so neither can the client. A 404 on
   * something previously visible is a legitimate permission change, not a bug.
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidation(): boolean {
    return this.status === 400;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Skips both the token and the refresh-and-retry path. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  // One retry, and only after a successful refresh. Retrying blindly on 401 turns an
  // expired session into an infinite loop against the server.
  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshSession();

    if (refreshed) {
      const retried = await send(path, options);
      return handle<T>(retried);
    }

    tokenStorage.clear();
    setAccessToken(null);
  }

  return handle<T>(response);
}

/**
 * Fetches a file rather than JSON.
 *
 * @remarks
 * Attachments cannot be loaded by pointing an `<img>` at this API: an image request carries
 * cookies, and this API is authenticated by a bearer token that only script can attach. So the
 * bytes are fetched here and handed to the page as an object URL.
 *
 * It goes through the same retry-after-refresh path as every other request, because an expired
 * session should reload a picture rather than break it.
 */
export async function apiBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  let response = await send(path, options);

  if (response.status === 401 && !options.anonymous && (await refreshSession())) {
    response = await send(path, options);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response.blob();
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Generated when absent so every request is traceable end to end, which is what makes
    // the correlation id in a ProblemDetails body worth quoting.
    'X-Correlation-Id': crypto.randomUUID(),
  };

  // FormData is passed through untouched, and deliberately without a Content-Type: the
  // browser sets multipart/form-data with the boundary it generated, and a hand-written header
  // omits that boundary and produces a body the server cannot parse.
  const isFormData = options.body instanceof FormData;

  if (options.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (!options.anonymous && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return fetch(`${BASE_PATH}${path}${buildQuery(options.query)}`, {
    method: options.method ?? 'GET',
    headers,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal ?? null,
  });
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  if (response.ok) {
    return (await response.json()) as T;
  }

  throw await toApiError(response);
}

async function toApiError(response: Response): Promise<ApiError> {
  let errorCode = 'System.Unexpected';
  let message = 'خطای غیرمنتظره‌ای رخ داد.';
  let correlationId: string | null = response.headers.get('X-Correlation-Id');
  let fieldErrors: Record<string, string[]> = {};

  try {
    const problem = (await response.json()) as {
      errorCode?: string;
      detail?: string;
      title?: string;
      correlationId?: string;
      errors?: Record<string, string[]>;
    };

    errorCode = problem.errorCode ?? errorCode;
    message = problem.detail ?? problem.title ?? message;
    correlationId = problem.correlationId ?? correlationId;
    fieldErrors = problem.errors ?? {};
  } catch {
    // A non-JSON body means a proxy or an infrastructure failure rather than the API. The
    // defaults above already describe that honestly.
  }

  return new ApiError({ status: response.status, errorCode, message, correlationId, fieldErrors });
}

/**
 * Exchanges the stored refresh token for a new session.
 *
 * Single-flight: several requests failing with 401 at once must produce one refresh. Because
 * refresh tokens rotate, a second concurrent refresh would present an already-rotated token,
 * which the server correctly treats as compromise and answers by revoking the whole family -
 * logging the user out for being fast.
 */
function refreshSession(): Promise<boolean> {
  // Cleared with `.finally()` on the promise, not in a `finally` block inside the body.
  //
  // A `finally` inside an async function runs the moment the body returns - and the body
  // returns before its first `await` whenever there is no stored token, or reading storage
  // throws, which is what a private window does. That cleared the latch *before* `??=` had
  // assigned to it, so the assignment then stored an already-resolved promise that nothing
  // would ever clear again. Every later 401 in the page's lifetime short-circuited to
  // "refresh failed" without asking the server, and a tab that lost its token once could not
  // recover by signing in again.
  //
  // On the promise, the callback cannot run until the promise settles, which is always after
  // the assignment.
  refreshInFlight ??= attemptRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function attemptRefresh(): Promise<boolean> {
  try {
    const refreshToken = tokenStorage.get();

    if (!refreshToken) {
      return false;
    }

    const response = await fetch(`${BASE_PATH}/auth/session/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const session = (await response.json()) as { accessToken: string; refreshToken: string };

    setAccessToken(session.accessToken);
    tokenStorage.set(session.refreshToken);

    return true;
  } catch {
    return false;
  }
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) {
    return '';
  }

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }

  const serialized = params.toString();

  return serialized ? `?${serialized}` : '';
}
